import React from "react";
import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  useTheme,
  Typography,
  FormGroup,
  Tooltip,
} from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

export const EGCheckbox = ({
  defaultChecked,
  checked,
  label = "",
  required = false,
  disabled = false,
  size = "small",
  color = "default",
  baseColor = "",
  checkedColor = "",
  checkedColorLabel = "secondary",
  name = "",
  value = "",
  id = "",
  labelPlacement = "end",
  onChange = () => {},
  sx = {},
  indeterminate = false,
  disableRipple = false,
  error = false,
  fontSize = 14,
  fontWeight = 600,
  helperText = "",
  typographyVariant = "h6",
  typographyProps = {},
  requiredTooltipText = "",
  ...props
}) => {
  const theme = useTheme();
  const [t] = useTranslation("components");

  const isCustomColor =
    color && !["default", "primary", "secondary"].includes(color);
  const customPalette = isCustomColor ? theme.palette[color] : null;

  const asteriskId = `required-asterisk-${
    name || id || Math.random().toString(36).substring(2, 7)
  }`;

  // helper: determina si una string parece un color CSS (#, rgb, hsl)
  const isCssColorString = (str) =>
    typeof str === "string" &&
    (/^#/.test(str) || /^rgb/.test(str) || /^hsl/.test(str));

  // determina si el checkbox está chequeado (soporta controlado y no-controlado)
  const isChecked = Boolean(
    typeof checked !== "undefined" ? checked : defaultChecked,
  );

  const resolvePaletteColor = (palNameOrColor) => {
    if (!palNameOrColor) return undefined;

    // si es color CSS directo, devuélvelo
    if (isCssColorString(palNameOrColor)) return palNameOrColor;

    // si existe en theme.palette, intenta devolver .main o el valor directo
    const pal = theme.palette[palNameOrColor];
    if (pal) return pal.main || pal;

    // si es string pero no coincide, devolver como fallback (podría ser una variable CSS)
    return palNameOrColor;
  };

  const getLabelColor = () => {
    // prioridad: disabled -> error -> checked color -> undefined (usa color por defecto del tema)
    if (disabled) return theme.palette.text.disabled;
    if (error) return theme.palette.error.main;

    if (!isChecked) {
      // no chequeado: si hay custom palette derivada de `color` usamos su main, si no, undefined
      return customPalette?.main || undefined;
    }

    // si está chequeado, priorizamos checkedColorLabel
    const resolved = resolvePaletteColor(checkedColorLabel);
    if (resolved) return resolved;

    // fallback al customPalette o prop color
    return customPalette?.main || theme.palette[color]?.main || undefined;
  };

  // Construye sx para el Checkbox (icono)
  // Prioridad: error -> checkedColor (si fue definido) -> customPalette -> vacío
  const checkboxSx = (() => {
    // si hay error -> estilo de error
    if (error) {
      return {
        color: theme.palette.error.main,
        "&.Mui-checked": {
          color: theme.palette.error.main,
        },
        "&:hover": {
          backgroundColor: theme.palette.error.main + "20",
        },
      };
    }

    // si se pasó checkedColor (nombre de paleta o css color), resolvemos y aplicamos en estado checked
    const resolvedCheckedColor = resolvePaletteColor(checkedColor);

    if (resolvedCheckedColor) {
      return {
        color: resolvedCheckedColor, // color por defecto (cuando esté chequeado o no, depende del override del tema)
        "&.Mui-checked": {
          color: resolvedCheckedColor,
        },
        "&:hover": {
          backgroundColor: resolvedCheckedColor + "20",
        },
        "&.Mui-focusVisible": {
          outline: `2px solid ${resolvedCheckedColor}`,
          outlineOffset: 1,
        },
        "&.Mui-disabled": {
          color: theme.palette.action.disabled,
        },
      };
    }

    // si no se pasó checkedColor, usar customPalette si existe
    if (customPalette) {
      return {
        color: customPalette.main,
        "&.Mui-checked": {
          color: customPalette.main,
        },
        "&:hover": {
          backgroundColor: customPalette.main + "20",
        },
        "&.Mui-focusVisible": {
          outline: `2px solid ${customPalette.main}`,
          outlineOffset: 1,
        },
        "&.Mui-disabled": {
          color: theme.palette.action.disabled,
        },
      };
    }

    return {};
  })();

  const labelComponent = (
    <Typography
      variant={typographyVariant}
      fontSize={fontSize}
      fontWeight={fontWeight}
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        color: getLabelColor(),
        transition: "color 0.3s ease-in-out",
      }}
      {...typographyProps}
    >
      {label}
      {required && (
        <Tooltip title={requiredTooltipText || t("Checkbox.required_field")}>
          <Typography
            component="span"
            id={asteriskId}
            aria-label={requiredTooltipText || t("Checkbox.required_field")}
            sx={{
              color: error
                ? theme.palette.error.main
                : theme.palette.text.secondary,
              marginLeft: "2px",
              cursor: "help",
              fontWeight: 600,
            }}
          >
            *
          </Typography>
        </Tooltip>
      )}
    </Typography>
  );

  return (
    <FormGroup>
      <FormControlLabel
        control={
          <Checkbox
            defaultChecked={defaultChecked}
            checked={checked}
            name={name}
            id={id}
            size={size}
            color={isCustomColor || error ? "default" : color}
            sx={{
              "& .MuiSvgIcon-root": {
                fontSize: 20,
              },
              ...checkboxSx,
              ...sx,
            }}
            onChange={onChange}
            indeterminate={indeterminate}
            disableRipple={disableRipple}
            disabled={disabled}
            slotProps={{
              input: { "aria-describedby": required ? asteriskId : undefined },
            }}
          />
        }
        label={labelComponent}
        disabled={disabled}
        value={value}
        labelPlacement={labelPlacement}
        {...props}
      />
      {helperText && (
        <FormHelperText
          sx={{
            color: error
              ? theme.palette.error.main
              : theme.palette.text.secondary,
            marginLeft: "14px",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </FormGroup>
  );
};

EGCheckbox.propTypes = {
  defaultChecked: PropTypes.bool,
  checked: PropTypes.bool,
  label: PropTypes.string,
  baseColor: PropTypes.string,
  checkedColor: PropTypes.string,
  checkedColorLabel: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  color: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  id: PropTypes.string,
  labelPlacement: PropTypes.oneOf(["end", "start", "top", "bottom"]),
  onChange: PropTypes.func,
  sx: PropTypes.object,
  indeterminate: PropTypes.bool,
  disableRipple: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  typographyVariant: PropTypes.string,
  typographyProps: PropTypes.object,
  requiredTooltipText: PropTypes.string,
};
